import Controller from '@ember/controller';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import ENV from 'donor-elf-web/config/environment';
import filterUtils from 'donor-elf-web/lib/filter-utils';
import numberUtils from 'donor-elf-web/lib/number-utils';
import { storageFor } from 'ember-local-storage';
import Settings from 'donor-elf-web/services/settings';
import Store from '@ember-data/store';
import { cached, tracked } from '@glimmer/tracking';
import type { FundSummaryRouteModel } from 'donor-elf-web/routes/funds/fund/funds-summary';
import FundGroupModel from 'donor-elf-web/models/fund-group';
import type { SafeString } from '@ember/template';
import FundSummaryModel from 'donor-elf-web/models/fund-summary';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';

interface ItemInterface {
  fundSummary: FundSummaryModel;
  fundGroupIds?: string[];
}

export default class FundsSummaryController extends Controller {
  declare model: FundSummaryRouteModel;
  @service declare media: any;
  @service declare settings: Settings;
  @service declare store: Store;
  @service declare storeCache: any;

  @storageFor('local-settings') localSettings: any;

  queryParams = ['filter', 'sortBy', 'sortDir', 'page'];

  @tracked items: ItemInterface[] = [];
  @tracked sortBy = 'fundSummary.fundNameLowerCase';
  @tracked sortDir = 'asc';
  @tracked filter = '';

  @tracked loading = true;
  @tracked loadingTotalPages?: number;
  @tracked loadingProgressBarStyle?: SafeString;
  @tracked filteredPercentBarStyle?: SafeString;
  @tracked error?: any;
  @tracked page = 1;
  perPage = 30;

  @cached
  get filterObject() {
    if (isEmpty(this.filter)) {
      return [[]];
    }
    return JSON.parse(decodeURIComponent(this.filter));
  }

  get showAnnualGoal() {
    return this.settings.userSettings?.showAnnualGoal || false;
  }

  get orgHasYTdDashboard() {
    return this.settings.orgSettings!.ytdStartAtMonth != null;
  }

  get canAdjustShowOptions() {
    return !this.orgHasYTdDashboard;
  }

  get fundGroups(): FundGroupModel[] {
    return this.model.fundGroups;
  }

  @cached
  get fundGroupIdsByFundId() {
    let byFundId: { [index: string]: string[] | undefined } = {};
    this.fundGroups.forEach((fundGroup) => {
      fundGroup.fundIds?.forEach((fundId) => {
        if (byFundId[fundId] == null) {
          byFundId[fundId] = [];
        }
        byFundId[fundId]!.push(fundGroup.id);
      });
    });
    return byFundId;
  }

  get filterSettings() {
    let filterSettings: any = [
      {
        prop: 'fundSummary.fundNameLowerCase',
        label: 'Fund Name',
        type: 'string',
        mapValFn: (val: any) => {
          return (val || '').toLowerCase();
        },
      },
    ];
    if (!this.settings.orgSettings!.incomeOnly) {
      filterSettings.push({
        prop: 'fundSummary.balance',
        label: 'Balance',
        type: 'number',
      });
    }
    if (this.orgHasYTdDashboard) {
      filterSettings = filterSettings.concat([
        {
          prop: 'fundSummary.ytdGoal.annualGoal',
          label: 'Annual Goal',
          type: 'number',
        },
        {
          prop: 'fundSummary.ytdGoal.totalPercent',
          label: 'Total Percent',
          type: 'number',
        },
        {
          prop: 'fundSummary.ytdGoal.recurringPledgesPercent',
          label: 'Recurring Percent',
          type: 'number',
        },
      ]);
    } else {
      if (this.showAnnualGoal) {
        filterSettings = filterSettings.concat([
          {
            prop: 'fundSummary.rollingGoal.annualGoal',
            label: 'Annual Goal',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.lastYearAnnualAmount',
            label: 'Annual Amount',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.lastYearAvgPercentage',
            label: 'Annual Amount %',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.pledgedAnnualAmount',
            label: 'Funded Amount',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.pledgedMonthlyPercent',
            label: 'Funded %',
            type: 'number',
          },
        ]);
      } else {
        filterSettings = filterSettings.concat([
          {
            prop: 'fundSummary.rollingGoal.monthlyGoal',
            label: 'Monthly Goal',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.lastYearAvgAmount',
            label: 'Monthly Average',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.lastYearAvgPercentage',
            label: 'Monthly Average %',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.pledgedMonthlyAmount',
            label: 'Funded Amount',
            type: 'number',
          },
          {
            prop: 'fundSummary.rollingGoal.pledgedMonthlyPercent',
            label: 'Funded %',
            type: 'number',
          },
        ]);
      }
    }
    if (this.fundGroups.length > 0) {
      filterSettings = filterSettings.concat([
        {
          prop: 'fundGroupIds',
          label: 'Fund Group',
          type: 'select',
          options: this.fundGroups.map((fundGroup) => {
            return { label: fundGroup.name, val: fundGroup.id };
          }),
        },
      ]);
    }
    return filterSettings;
  }

  get chartHeight() {
    return this.media.isSmallDown ? 100 : 200;
  }

  get canShowBalance() {
    return !this.settings.orgSettings!.incomeOnly && !this.settings.orgSettings!.hideMonthlyBalance;
  }

  get canShowExpense() {
    return !this.settings.orgSettings!.incomeOnly;
  }

  async loadFundSummariesPage(page: number) {
    let loadingPercentage = 5;
    if (this.loadingTotalPages && page > 1) {
      loadingPercentage = Math.round(
        ((page - 1) * 100) / this.loadingTotalPages,
      );
    }
    if (loadingPercentage < 5) {
      loadingPercentage = 5;
    }
    this.loadingProgressBarStyle = htmlSafe(`width: ${loadingPercentage}%`);

    try {
      let fundSummaries = await this.store.query('fund-summary', { page });
      this.items = [
        ...this.items,
        ...fundSummaries.map((fundSummary) => {
          return {
            fundSummary,
            fundGroupIds: this.fundGroupIdsByFundId[fundSummary.fundId],
          };
        }),
      ];
      this.loadingTotalPages = fundSummaries.meta.total_pages;

      if (page < fundSummaries.meta.total_pages) {
        this.loadFundSummariesPage(page + 1);
      } else {
        this.setFilteredPercentBarStyle();
        this.loading = false;
      }
    } catch (error: any) {
      this.error = error;
    }
  }

  get filteredItems(): ItemInterface[] {
    return filterUtils.getFilteredResults({
      filters: this.filterObject,
      filterSettings: this.filterSettings,
      items: this.items,
      itemIdProp: 'fundSummary.id',
    });
  }

  get filterApplied() {
    return this.filteredItems.length < this.items.length;
  }
  get filteredPercent() {
    return numberUtils.roundPrecision(
      (this.filteredItems.length * 100) / this.items.length,
      1,
    );
  }

  setFilteredPercentBarStyle() {
    this.filteredPercentBarStyle = htmlSafe(`width: ${this.filteredPercent}%`);
  }

  get sortedItems() {
    return sortByProperties(this.filteredItems, [
      `${this.sortBy}:${this.sortDir}`,
    ]);
  }

  get pagedItems() {
    let offset = (this.page - 1) * this.perPage;
    return this.sortedItems.slice(offset, offset + this.perPage);
  }
  get totalPages() {
    return Math.ceil(this.sortedItems.length / this.perPage);
  }

  get rootURL() {
    return (ENV as any).rootURL;
  }

  @action
  templateDidInsert() {
    this.loadFundSummariesPage(1);
  }

  @action
  templateWillDestroy() {
    this.sortBy = 'fundSummary.fundNameLowerCase';
    this.sortDir = 'asc';
    this.filter = '';
    this.loading = true;
    this.loadingTotalPages = undefined;
    this.error = undefined;
    this.items = [];
    this.page = 1;
  }

  @action
  handleSortBy(property: string) {
    if (this.sortBy === property) {
      if (this.sortDir === 'asc') {
        this.sortDir = 'desc';
      } else {
        this.sortDir = 'asc';
      }
    } else {
      this.sortBy = property;
      this.sortDir = 'desc';
    }
    this.page = 1;
  }

  @action
  handleApplyFilter(filters: any) {
    this.loading = true;
    this.page = 1;
    this.filter = encodeURIComponent(JSON.stringify(filters));
    // showing loading screen for 50 ms to show the user the filter has been
    // applied
    setTimeout(() => {
      this.setFilteredPercentBarStyle();
      this.loading = false;
    }, 50);
  }

  @action
  toggle(property: any) {
    this.toggleProperty(property);
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
  }
}
